import { Box, Typography } from '@mui/material';

import { PLAN_COLORS } from '../constants';
import type { PlanConfig } from '../types';

interface LeadSourceTimelineProps {
  config: PlanConfig;
}

interface Segment {
  width: number;
  color: string;
  label: string;
}

/** Mini past / both / new bar that visualises when each lead source is active. */
export const LeadSourceTimeline = ({ config }: LeadSourceTimelineProps) => {
  const horizon = Math.max(config.ramp + 30, config.newStart + 30, 120);
  const pastEnd = config.ramp;
  const newEnd = horizon;
  const overlap = Math.max(0, Math.min(pastEnd, newEnd) - Math.max(1, config.newStart));
  const pastOnly = Math.max(0, Math.min(pastEnd, config.newStart - 1));
  const newOnly = Math.max(0, newEnd - Math.max(pastEnd, config.newStart));

  const overlapW = Math.round((overlap / horizon) * 100);
  const pastOnlyW = Math.round((pastOnly / horizon) * 100);
  const newOnlyW = Math.round((newOnly / horizon) * 100);
  const filled = pastOnlyW + overlapW + newOnlyW;

  const segments: Segment[] = [
    { width: pastOnlyW, color: PLAN_COLORS.past, label: 'Past' },
    { width: overlapW, color: PLAN_COLORS.both, label: 'Both' },
    { width: newOnlyW, color: PLAN_COLORS.newLeads, label: 'New' },
  ].filter((s) => s.width > 0);

  const legend = [
    { color: PLAN_COLORS.past, label: 'Past leads (lookback)' },
    { color: PLAN_COLORS.newLeads, label: 'New unsold leads' },
    { color: PLAN_COLORS.both, label: 'Both running' },
  ];

  return (
    <Box
      sx={{
        mt: 0.5,
        bgcolor: 'grey.50',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 1,
      }}
    >
      <Typography sx={{ fontSize: 10, color: 'text.secondary', mb: 0.5, fontWeight: 500 }}>
        Lead source timeline
      </Typography>
      <Box
        sx={{
          display: 'flex',
          height: 20,
          borderRadius: 0.75,
          overflow: 'hidden',
          mb: 0.5,
        }}
      >
        {segments.map((s, i) => (
          <Box
            key={`${s.label}-${i}`}
            sx={{
              width: `${s.width}%`,
              minWidth: s.width > 5 ? 20 : 0,
              bgcolor: s.color,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 9,
              fontWeight: 700,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
          >
            {s.width > 8 ? s.label : ''}
          </Box>
        ))}
        {filled < 100 && <Box sx={{ width: `${100 - filled}%`, bgcolor: 'grey.300' }} />}
      </Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {legend.map((l) => (
          <Box key={l.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: 0.5, bgcolor: l.color }} />
            <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>{l.label}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
